<script setup lang="ts">
import { ref, onMounted } from 'vue';
import logoUrl from "../../img/favicon-sport.png";
import LogoEpisod from "./Episod/Utils/LogoEpisod.vue";
import LogoSanctuary from "./Sanctuary/Utils/LogoSanctuary.vue";
import LoginModal from "./Utils/LoginModal.vue";
import { useGlobalState } from '@/composables/useGlobalState';

const { userData, fetchUser } = useGlobalState();
const showLoginModal = ref(false);

onMounted(() => {
    if (localStorage.getItem('apiToken')) {
        fetchUser();
    }
});
</script>

<template>
  <div class="sport-home">
    <!-- Navbar -->
    <nav class="fixed top-0 left-0 right-0 z-50 p-4">
      <div class="max-w-7xl mx-auto flex justify-between items-center bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl px-6 py-3">
        <div class="flex items-center gap-2">
            <img :src="logoUrl" alt="Logo" class="w-8 h-8">
            <span class="fw-bold tracking-tight">Codifyo Sport</span>
        </div>
        <div class="flex items-center gap-4">
            <template v-if="userData">
                <router-link :to="{ name: 'sport_account' }" class="btn btn-sm btn-outline-light border-0 opacity-75 hover-opacity-100 d-flex align-items-center gap-2">
                    <i class="fas fa-user-circle"></i>
                    Mon Compte
                </router-link>
            </template>
            <template v-else>
                <button @click="showLoginModal = true" class="btn btn-sm btn-light rounded-pill px-4 fw-bold">
                    Connexion
                </button>
            </template>
        </div>
      </div>
    </nav>

    <div class="hero-section mt-5">
      <div class="logo-area">
        <img :src="logoUrl" alt="Sport Logo" class="hero-logo" />
        <h1>Codifyo Sport</h1>
      </div>
      <p class="subtitle">Atteignez vos objectifs, suivez vos progrès.</p>
    </div>

    <div class="dashboard-grid">
      <router-link :to="{ name: 'episod' }" class="nav-card">
        <div class="card-icon">
          <i class="fas fa-running"></i>
        </div>
        <div class="card-content">
          <h3>Suivi Episod</h3>
          <p>Consultez vos sessions de sport et vos statistiques de performance.</p>
        </div>
        <div class="card-arrow">
          <i class="fas fa-chevron-right"></i>
        </div>
      </router-link>

      <router-link :to="{ name: 'sanctuary' }" class="nav-card">
        <div class="card-icon sanctuary">
          <i class="fas fa-dumbbell"></i>
        </div>
        <div class="card-content">
          <h3>Sanctuary Gym</h3>
          <p>Gérez vos programmes d'entraînement et votre progression en salle.</p>
        </div>
        <div class="card-arrow">
          <i class="fas fa-chevron-right"></i>
        </div>
      </router-link>
    </div>

    <LoginModal :is-open="showLoginModal" @close="showLoginModal = false" />
  </div>
</template>

<style scoped>
.sport-home {
  min-height: 100vh;
  background: radial-gradient(circle at top right, #2d3748, #1a202c);
  color: white;
  padding: 4rem 2rem;
  font-family: 'Inter', sans-serif;
}

.hero-section {
  text-align: center;
  margin-bottom: 4rem;
}

.logo-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.hero-logo {
  height: 100px;
  width: 100px;
  filter: drop-shadow(0 0 15px rgba(255, 165, 0, 0.4));
}

.hero-section h1 {
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(to right, #ff9f43, #ffcc33);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
}

.subtitle {
  font-size: 1.25rem;
  color: #a0aec0;
  max-width: 600px;
  margin: 0 auto;
}

.dashboard-grid {
  max-width: 900px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
}

.nav-card {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  text-decoration: none;
  color: white;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  cursor: pointer;
}

.nav-card:hover {
  transform: translateY(-10px);
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 165, 0, 0.3);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.card-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #ff9f43, #ff6b6b);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.card-icon.sanctuary {
  background: linear-gradient(135deg, #4facfe, #00f2fe);
}

.card-content {
  flex: 1;
}

.card-content h3 {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
}

.card-content p {
  font-size: 0.95rem;
  color: #a0aec0;
  line-height: 1.4;
  margin: 0;
}

.card-arrow {
  color: #4a5568;
  font-size: 1.2rem;
  transition: transform 0.3s ease;
}

.nav-card:hover .card-arrow {
  transform: translateX(5px);
  color: #ff9f43;
}

@media (max-width: 600px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
}
</style>
